import { useMemo } from "react";

import { OnOffSwitch } from "@similarweb/ui-components";

import { CrmManagementDisabledTooltip } from "app/pages/sales-intelligence/common-components/CrmManagementDisabledTooltip";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import OptionsMenu from "app/pages/sales-intelligence/common-components/options-menu/OptionsMenu/OptionsMenu";
import type { MenuItemOption } from "app/pages/sales-intelligence/common-components/options-menu/types";
import { useCrmManagementReadOnly } from "app/pages/sales-intelligence/hooks/useCrmManagementReadOnly";
import { useIsIndustryTaxonomyOptedIn } from "app/pages/sales-intelligence/modules/industry/hooks/useIsIndustryTaxonomyOptedIn";

import type { CrmEnrichmentJob, CrmProvider } from "../../types/types";
import { hasIndustryEnrichmentMapping } from "../../utils/mappingValidation";
import createCrmEnrichmentI18nKeys from "../../utils/translations";
import { CrmEnrichmentJobIndustryMappingWarning } from "../CrmEnrichmentJobIndustryMappingWarning/CrmEnrichmentJobIndustryMappingWarning";
import CrmEnrichmentJobRunErrorBanner from "../CrmEnrichmentJobRunErrorBanner/CrmEnrichmentJobRunErrorBanner";
import CrmEnrichmentJobRunStatus from "../CrmEnrichmentJobRunStatus/CrmEnrichmentJobRunStatus";

import { getCRMEnrichmentMenuItems } from "./const";
import {
    StyledEnrichmentJobInfo,
    StyledEnrichmentJobLabel,
    StyledEnrichmentJobNameWrapper,
    StyledEnrichmentJobObject,
    StyledEnrichmentJobType,
    StyledEnrichmentJobTypeDivider,
    StyledEnrichmentSwitchContainer,
    StyledEnrichmentToggle,
    StyledEnrichmentToggleWrapperFlex,
} from "./styles";

export type CrmEnrichmentToggleProps = {
    job: CrmEnrichmentJob;
    crmProvider: CrmProvider;
    onRename: () => void;
    onEdit: (jobId: string) => void;
    onLogs: (jobId: string) => void;
    onDelete: () => void;
    onViewLogFile: (jobId: string, runId: string, isError: boolean) => void;
    onSelectOption?: (option: MenuItemOption) => void;
    onToggleMenu?: (isOpen: boolean, jobName: string) => void;
    onToggle: (jobId: string, value: boolean) => void;
};

const CrmEnrichmentToggle = ({
    job,
    crmProvider,
    onToggle,
    onEdit,
    onLogs,
    onRename,
    onDelete,
    onViewLogFile,
    onToggleMenu,
    onSelectOption,
}: CrmEnrichmentToggleProps) => {
    const {
        active,
        name,
        type,
        enrichmentObject,
        id,
        status,
        lastRunOutcome,
        lastRunDate,
        lastRunId,
        frequencyInMinutes,
    } = job;

    const isIndustryTaxonomyOptedIn = useIsIndustryTaxonomyOptedIn();
    const { isReadOnly, disabledTooltip } = useCrmManagementReadOnly();
    const showsIndustryMappingWarning = !isIndustryTaxonomyOptedIn && hasIndustryEnrichmentMapping(job.mappings);

    const i18nKeys = createCrmEnrichmentI18nKeys(crmProvider);

    const handleToggleClick = () => {
        if (isReadOnly) {
            return;
        }

        onToggle(id, !active);
    };

    const handleToggleMenuClick = (isOpen: boolean) => {
        if (isOpen && onToggleMenu) {
            onToggleMenu(isOpen, name);
        }
    };

    const handleSelectOption = (option: MenuItemOption) => {
        if (isReadOnly && option !== "logs") {
            return;
        }

        onSelectOption?.(option);

        switch (option) {
            case "rename":
                onRename();
                break;

            case "delete":
                onDelete();
                break;

            case "edit":
                onEdit(id);
                break;

            case "logs":
                onLogs(id);
                break;
        }
    };

    const handleViewLogFile = () => {
        if (lastRunId) {
            onViewLogFile(id, lastRunId, lastRunOutcome === "failed");
        }
    };

    const shouldHideStatus = lastRunOutcome === "failed";
    const instantJobRunStatus = lastRunDate ? "success" : null;

    const orderedMenuOptions = useMemo(
        () => getCRMEnrichmentMenuItems(!!lastRunId, isReadOnly),
        [isReadOnly, lastRunId],
    );
    const renderToggle = (readOnly: boolean) => (
        <OnOffSwitch
            isSelected={active}
            onClick={handleToggleClick}
            isDisabled={readOnly}
        />
    );

    return (
        <StyledEnrichmentToggleWrapperFlex
            flexDirection="column"
            gap="8px"
        >
            <StyledEnrichmentToggle>
                <StyledEnrichmentSwitchContainer>
                    <CrmManagementDisabledTooltip renderContent={renderToggle} />

                    <StyledEnrichmentJobNameWrapper alignItems="center">
                        <StyledEnrichmentJobLabel>{name}</StyledEnrichmentJobLabel>
                        {showsIndustryMappingWarning ? <CrmEnrichmentJobIndustryMappingWarning /> : null}
                    </StyledEnrichmentJobNameWrapper>
                </StyledEnrichmentSwitchContainer>

                <StyledEnrichmentJobInfo>
                    <StyledEnrichmentJobInfo>
                        <I18n
                            translatable={{ key: i18nKeys.type(type) }}
                            element={StyledEnrichmentJobType}
                        />

                        <StyledEnrichmentJobTypeDivider />

                        <I18n
                            translatable={{ key: i18nKeys.object(enrichmentObject) }}
                            element={StyledEnrichmentJobObject}
                        />
                    </StyledEnrichmentJobInfo>

                    <OptionsMenu
                        id={`${crmProvider}-enrichment-job-${id}`}
                        items={orderedMenuOptions}
                        onSelect={handleSelectOption}
                        onToggle={handleToggleMenuClick}
                        tooltipContent={<I18n translatable={{ key: "si.common.more-options" }} />}
                        disabledTooltipContent={disabledTooltip}
                    />
                </StyledEnrichmentJobInfo>
            </StyledEnrichmentToggle>

            {!shouldHideStatus ? (
                <CrmEnrichmentJobRunStatus
                    type={type}
                    status={type === "scheduled" ? status : instantJobRunStatus}
                    lastRunDate={lastRunDate}
                    frequencyInMinutes={frequencyInMinutes}
                />
            ) : null}

            <CrmEnrichmentJobRunErrorBanner
                lastRunOutcome={lastRunOutcome}
                onViewLogFile={handleViewLogFile}
            />
        </StyledEnrichmentToggleWrapperFlex>
    );
};

export default CrmEnrichmentToggle;
